import { Consumer, createConsumer, Subscription } from "@rails/actioncable";
import * as Y from "yjs";
import { WebsocketProvider } from "@y-rb/actioncable";
import { AwarenessUser, InitialAwarenessUser } from "../AwarenessUsers/types";
import { syncFormYDocs, EventEmitter } from "@getregistered/greg-editors";
import type { FormAssistantEvents, SyncResult } from "@getregistered/greg-editors";
import { AwarenessControl } from "../AwarenessUsers/AwarenessControl";
import { TrackUpdateMessage } from "./types";
import { FormAssistantChannelControl } from "./FormAssistantChannelControl";
import { getConsumer, getSubscription } from "../actionCableConsumerPool";

export type ReadyState = "initializing" | "connecting" | "syncing" | "ready" | "disconnected";

export class DisconnectedError extends Error {
  constructor(action: string) {
    super(`Cannot ${action} while disconnected`);
    this.name = "DisconnectedError";
  }
}

export interface LoadingTimings {
  initStarted?: number;
  connectStarted?: number;
  connectCompleted?: number;
  syncStarted?: number;
  syncCompleted?: number;
  totalDuration?: number;
}

export interface FormChannelControlArgs {
  id: number;
  eventId: number;
  tracksEnabled: boolean;
  awarenessUser: AwarenessUser;
  cableUrl?: string;
  assistantEnabled?: boolean;
  initialState?: string;
}

let singleInstance: FormChannelControl;

export class FormChannelControl {
  ydoc: Y.Doc;
  id: number;
  eventId: number;
  tracksEnabled: boolean;
  initialAwarenessUser: InitialAwarenessUser;
  awarenessControl: AwarenessControl;
  consumer: Consumer;
  provider: WebsocketProvider;
  persistenceChannelSubscription: Subscription;
  assistantChat?: FormAssistantChannelControl;
  assistantEvents?: EventEmitter<FormAssistantEvents>;
  tracksChannelSubscription?: Subscription;
  lastTrackUpdate?: number;
  baseDocument: Y.Doc | null;
  private baseLoadPromise: Promise<void> | null = null;
  private baseDocumentResolver: (() => void) | null = null;
  // Timeout backing the in-flight base load. Cleared on success so a completed
  // load's stale timer can't later null resolver state belonging to a newer
  // in-flight load (GR-2124, paulodeon finding 7).
  private baseLoadTimer: ReturnType<typeof setTimeout> | null = null;
  // Which action started the in-flight base load. "load_base_for_diff" does not
  // capture a FormConfigSnapshot, so a later reset() (which needs "load_base")
  // must not silently piggyback on it (GR-2124).
  private pendingBaseLoadAction: "load_base" | "load_base_for_diff" | null = null;
  // Whether the currently cached baseDocument was fetched via a snapshot-less
  // "load_base_for_diff". A later reset() needs the FormConfigSnapshot only
  // "load_base" captures, so it must still fire one even though the base is
  // already cached (GR-2124, paulodeon finding 6).
  private baseDocumentFromDiffLoad = false;
  trackUpdateListeners: Array<(lastUpdate: number) => void> = [];
  sessionsChannelSubscription?: Subscription;
  lastSessionUpdate?: number;
  sessionsUpdateListeners: Array<(lastUpdate: number) => void> = [];
  // Loading state tracking
  private _readyState: ReadyState = "initializing";
  private _timings: LoadingTimings = {};
  private readyStateListeners: Array<(state: ReadyState, timings: LoadingTimings) => void> = [];
  private syncCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private syncedObserverCleanup: (() => void) | null = null;
  private browserConnectivityCleanup: (() => void) | null = null;
  constructor({
    id,
    eventId,
    tracksEnabled,
    awarenessUser,
    cableUrl,
    assistantEnabled = false,
    initialState,
  }: FormChannelControlArgs) {
    // Start timing instrumentation
    this._timings.initStarted = performance.now();
    this.setReadyState("initializing");
    console.debug("[FormEditor] Initializing FormChannelControl", { formId: id, eventId });

    this.id = id;
    this.eventId = eventId;
    this.tracksEnabled = tracksEnabled;
    this.trackUpdateListeners = [];
    // GR-2123/GR-2203: garbage collection ON so tombstones are collected and
    // the server Y.Doc doesn't bloat (originally org-gated, now global).
    this.ydoc = new Y.Doc({ gc: true });
    this.baseDocument = null;

    // Pre-apply initial state from server-rendered HTML if available.
    // This seeds the Y.Doc so the WebSocket sync only transfers diffs
    // instead of the full document (~75-86KB), avoiding slow client disconnects.
    if (initialState) {
      try {
        const bytes = Uint8Array.from(atob(initialState), (c) => c.charCodeAt(0));
        Y.applyUpdate(this.ydoc, bytes);

        const baseDoc = new Y.Doc({ gc: true });
        baseDoc.transact(() => {
          Y.applyUpdate(baseDoc, bytes);
        });
        this.baseDocument = baseDoc;

        console.debug("[FormEditor] Pre-applied initial state from HTML", {
          bytes: bytes.length,
        });
      } catch (e) {
        console.warn("[FormEditor] Failed to apply initial state, falling back to WebSocket sync", e);
      }
    }

    // Transition to connecting state
    this._timings.connectStarted = performance.now();
    this.setReadyState("connecting");

    this.consumer = getConsumer(cableUrl);
    this.provider = new WebsocketProvider(
      this.ydoc,
      this.consumer,
      "FormChannel",
      {
        id: id.toString(),
      }
    );

    // The @y-rb/actioncable provider calls channel.send() fire-and-forget
    // (the returned Promise is never awaited). When the channel is closed
    // (e.g. after SubscriptionTimeoutError), Channel.perform() rejects with
    // "Channel is not subscribed" as an unhandled promise rejection.
    // Patch the subscription's send() to swallow these during disconnection.
    const providerSub = this.provider.channel;
    if (providerSub) {
      const innerChannel = (providerSub as any).channel;
      (providerSub as any).send = function (data: unknown) {
        innerChannel?.send?.(data)?.catch?.(() => {});
      };
    }

    // Poll for sync and monitor ongoing connection health
    this.monitorConnection();
    this.observeBrowserConnectivity();

    this.initialAwarenessUser = awarenessUser;
    this.awarenessControl = new AwarenessControl(
      this.provider,
      this.initialAwarenessUser
    );
    this.persistenceChannelSubscription = getSubscription(
      this.consumer,
      "FormPersistenceChannel",
      id,
      {
        received: this.receiveBaseDocument.bind(this),
      }
    );
    if (assistantEnabled) {
      this.assistantEvents = new EventEmitter<FormAssistantEvents>();
      this.assistantChat = new FormAssistantChannelControl(
        this.consumer,
        id,
        this.assistantEvents
      );
    }
    if (this.tracksEnabled) {
      this.tracksChannelSubscription = getSubscription(
        this.consumer,
        "EventTracksChannel",
        eventId,
        {
          received: this.receiveTracksUpdate.bind(this),
        }
      );
    }
    // Add session updates subscription
    this.sessionsChannelSubscription = getSubscription(
      this.consumer,
      "EventSessionsChannel",
      eventId,
      {
        received: this.receiveSessionsUpdate.bind(this),
      }
    );

    // Debug API for DevTools console (development only)
    if (process.env.NODE_ENV !== "production") {
    (window as any).__formEditorDebug = {
      getState: () => ({
        readyState: this._readyState,
        isDirty: this.ydoc.getMap("editorState").get("isDirty") ?? false,
        formId: this.id,
        eventId: this.eventId,
        hasBaseDocument: this.baseDocument !== null,
      }),
      getBaseState: () => {
        if (!this.baseDocument) return null;
        return this.baseDocument.getMap<boolean>("itemIdMap").toJSON();
      },
      getDiff: () => {
        if (!this.baseDocument) return { error: "No base document loaded" };
        const baseItemIds = new Set(this.baseDocument.getMap<boolean>("itemIdMap").keys());
        const currentItemIds = new Set(this.ydoc.getMap<boolean>("itemIdMap").keys());
        const allKeys = new Set([...baseItemIds, ...currentItemIds]);
        const diff: Record<string, string> = {};
        for (const key of allKeys) {
          const inBase = baseItemIds.has(key);
          const inCurrent = currentItemIds.has(key);
          if (inBase && !inCurrent) {
            diff[key] = "deleted";
          } else if (!inBase && inCurrent) {
            diff[key] = "added";
          } else {
            const replacer = (_k: string, v: unknown) => typeof v === "bigint" ? Number(v) : v;
            const baseJSON = JSON.stringify(this.baseDocument.getMap(key).toJSON(), replacer);
            const currentJSON = JSON.stringify(this.ydoc.getMap(key).toJSON(), replacer);
            if (baseJSON !== currentJSON) {
              diff[key] = "modified";
            }
          }
        }
        return Object.keys(diff).length === 0 ? { clean: true } : diff;
      },
    };
    }
  }
  receiveBaseDocument(data: number[] | { type: string }): void {
    // Notification from broadcast_save — the saved DB state has changed.
    if (!Array.isArray(data) && data.type === "saved") {
      // The previously loaded base is now stale.
      this.baseDocument = null;
      // GR-2124: re-derive isDirty against the freshly-saved state instead of
      // clearing it unconditionally. broadcast_save fans out to *every* editor
      // on the form (and to this editor even for a collaborator's save), so a
      // blind clear wiped the unsaved-changes flag for any local edit that was
      // not part of that save — e.g. a field deleted in the race window
      // between the server's dirty-clear and this broadcast — hiding Save and
      // Reset while the Y.Doc was still dirty until a page reload.
      this.reconcileDirtyAfterSave();
      return;
    }

    if (!Array.isArray(data)) return;

    // Full state from subscribe or load_base — apply it
    const bytes = new Uint8Array(data);
    const baseDocument = new Y.Doc({ gc: true });
    baseDocument.transact(() => {
      Y.applyUpdate(baseDocument, bytes);
    });
    this.baseDocument = baseDocument;

    // Resolve any pending lazy-load request
    if (this.baseDocumentResolver) {
      // Finding 7: clear the load's timeout so it can't fire later and orphan a
      // subsequent in-flight load's resolver state.
      if (this.baseLoadTimer) {
        clearTimeout(this.baseLoadTimer);
        this.baseLoadTimer = null;
      }
      // Finding 6: remember whether this base came from a snapshot-less diff
      // load, so a later reset() knows it still owes a FormConfigSnapshot.
      this.baseDocumentFromDiffLoad =
        this.pendingBaseLoadAction === "load_base_for_diff";
      this.baseDocumentResolver();
      this.baseDocumentResolver = null;
      this.baseLoadPromise = null;
      this.pendingBaseLoadAction = null;
    }
  }

  receiveTracksUpdate(data: TrackUpdateMessage): void {
    this.lastTrackUpdate = data.last_update_ts;
    this.trackUpdateListeners.forEach((listener) =>
      listener(data.last_update_ts)
    );
  }

  save(): void {
    if (this._readyState === "disconnected") {
      throw new DisconnectedError("save");
    }
    // perform() is async but ActionCableSubscription discards the promise.
    // Catch to prevent unhandled rejection if channel closed between our
    // readyState check and the actual send.
    Promise.resolve(this.provider.channel.perform("save")).catch((err) => {
      console.warn("[FormEditor] save perform failed (channel likely closed):", err?.message);
    });
  }

  async reset(): Promise<void> {
    if (this._readyState === "disconnected") {
      throw new DisconnectedError("reset");
    }
    // "load_base" captures a FormConfigSnapshot of the pre-reset DB state.
    const base = await this.ensureBaseDocument("load_base");
    if (!base) {
      console.error("[FormEditor] Failed to load base document for reset");
      return;
    }
    const syncResult = syncFormYDocs(base, this.ydoc);
    this.ydoc.getMap("editorState").set("isDirty", false);
    console.debug("[FormEditor] Reset completed", {
      formId: this.id,
      added: syncResult.added,
      updated: syncResult.updated,
      deleted: syncResult.deleted,
      unchanged: syncResult.unchanged,
    });
  }

  // Re-derive isDirty by comparing the working Y.Doc against the freshly
  // saved base. Clears isDirty only when there are genuinely no unsaved
  // changes; leaves Save/Reset visible when local edits diverge from what was
  // saved. Best-effort: on failure the flag is left untouched so unsaved work
  // is never silently hidden (GR-2124).
  private async reconcileDirtyAfterSave(): Promise<void> {
    try {
      // "load_base_for_diff" fetches the saved base without writing a
      // FormConfigSnapshot per editor per save (the save already captured one).
      const base = await this.ensureBaseDocument("load_base_for_diff");
      if (!base) return;
      const dirty = this.hasUnsavedChanges(base);
      const editorState = this.ydoc.getMap("editorState");
      if (editorState.get("isDirty") !== dirty) {
        // When re-dirtying after a save, tag the write with the saveVersion it
        // was derived against. This is an authoritative post-save
        // re-derivation, not a pre-save leftover, so the server's
        // stale_dirty_revert? one-shot must NOT suppress it. Otherwise the
        // saver's own save racing a local edit (the original GR-2124 repro)
        // would get isDirty flipped back to false and re-hide Save/Reset.
        if (dirty) {
          // GR-2381: saveVersion is authored server-side by y-rb, whose
          // integers decode in JS as BigInt — and Y.Map cannot store BigInt
          // ("Unexpected content type"). Coerce before writing. Tag BEFORE
          // isDirty so a throw can't half-apply the transact and leave an
          // untagged dirty flag that stale_dirty_revert? then mishandles.
          const saveVersion = Number(editorState.get("saveVersion") ?? 0);
          this.ydoc.transact(() => {
            editorState.set("dirtyReconciledSaveVersion", saveVersion);
            editorState.set("isDirty", true);
          });
        } else {
          editorState.set("isDirty", false);
        }
      }
      console.debug("[FormEditor] Save acknowledged, isDirty reconciled", { dirty });
    } catch (e) {
      console.warn(
        "[FormEditor] Failed to reconcile isDirty after save; leaving flag unchanged",
        e
      );
    }
  }

  // Lazily fetch the last-saved base document from the server, reusing an
  // in-flight request if one is already pending. Returns null on timeout.
  private async ensureBaseDocument(
    action: "load_base" | "load_base_for_diff" = "load_base"
  ): Promise<Y.Doc | null> {
    if (this.baseDocument === null) {
      if (!this.baseLoadPromise) {
        this.pendingBaseLoadAction = action;
        this.baseLoadPromise = new Promise<void>((resolve, reject) => {
          this.baseDocumentResolver = resolve;
          this.baseLoadTimer = setTimeout(() => {
            this.baseDocumentResolver = null;
            this.baseLoadPromise = null;
            this.baseLoadTimer = null;
            this.pendingBaseLoadAction = null;
            reject(new Error("load_base timed out"));
          }, 10_000);
        });
        this.persistenceChannelSubscription.perform(action);
      } else if (
        action === "load_base" &&
        this.pendingBaseLoadAction === "load_base_for_diff"
      ) {
        // A snapshot-less diff load (from reconcileDirtyAfterSave) is already in
        // flight. reset() needs the pre-reset FormConfigSnapshot that only
        // "load_base" captures, so trigger it for its snapshot side effect while
        // still awaiting the in-flight promise for the document itself. Upgrade
        // the tracked action so a second reset doesn't re-fire (GR-2124).
        this.pendingBaseLoadAction = "load_base";
        this.persistenceChannelSubscription.perform("load_base");
      }
      await this.baseLoadPromise;
    } else if (action === "load_base" && this.baseDocumentFromDiffLoad) {
      // Finding 6: the cached base came from a snapshot-less diff load
      // (reconcileDirtyAfterSave — now the common path: save -> baseDocument=null
      // -> diff load caches it). A reset() short-circuits on that cached base, so
      // "load_base" never fires and no FormConfigSnapshot of the pre-reset state
      // is taken. Fire it now for its snapshot side effect; the cached base is
      // still valid to diff against, so we neither null it nor await the reload.
      this.baseDocumentFromDiffLoad = false;
      this.persistenceChannelSubscription.perform("load_base");
    }
    return this.baseDocument;
  }

  // Whether the working Y.Doc has field changes relative to the saved base.
  // Uses the same authoritative comparator as reset() (semantic, tombstone-
  // and representation-aware) rather than a raw JSON compare, which would flag
  // spurious diffs between the CRDT working doc and the DB-derived base. Runs
  // on a throwaway clone so the working doc is never mutated.
  //
  // GR-2123: this stays `gc: false` (unlike every other Y.Doc here) by design.
  // The clone is never persisted or broadcast, so it can't contribute to the
  // server-side Y.Doc bloat gc exists to bound; it lives for one synchronous
  // diff and is discarded. gc off keeps its state a faithful,
  // tombstone-preserving mirror of the working doc for syncFormYDocs.
  private hasUnsavedChanges(base: Y.Doc): boolean {
    const clone = new Y.Doc({ gc: false });
    Y.applyUpdate(clone, Y.encodeStateAsUpdate(this.ydoc));
    const diff = syncFormYDocs(base, clone);
    return diff.added + diff.updated + diff.deleted > 0;
  }

  addTrackUpdateListener(listener: (lastUpdate: number) => void): void {
    this.trackUpdateListeners.push(listener);
  }

  receiveSessionsUpdate(data: { last_update_ts: number }): void {
    this.lastSessionUpdate = data.last_update_ts;
    this.sessionsUpdateListeners.forEach((listener) =>
      listener(data.last_update_ts)
    );
  }

  addSessionsUpdateListener(listener: (lastUpdate: number) => void): void {
    this.sessionsUpdateListeners.push(listener);
  }

  // Ready state management
  get readyState(): ReadyState {
    return this._readyState;
  }

  get timings(): LoadingTimings {
    return { ...this._timings };
  }

  private setReadyState(state: ReadyState): void {
    if (this._readyState !== state) {
      this._readyState = state;
      console.debug("[FormEditor] State transition:", state);
      this.readyStateListeners.forEach((listener) =>
        listener(state, this._timings)
      );
    }
  }

  addReadyStateListener(
    listener: (state: ReadyState, timings: LoadingTimings) => void
  ): void {
    this.readyStateListeners.push(listener);
    // Immediately call with current state
    listener(this._readyState, this._timings);
  }

  removeReadyStateListener(
    listener: (state: ReadyState, timings: LoadingTimings) => void
  ): void {
    const index = this.readyStateListeners.indexOf(listener);
    if (index > -1) {
      this.readyStateListeners.splice(index, 1);
    }
  }

  private monitorConnection(): void {
    const FAST_INTERVAL = 50; // 50ms during initial sync
    // GR-2394 (xocs review): once the connection is flagged disconnected the fast
    // poll has nothing left to earn — the 20Hz timer would otherwise run forever
    // on a dead connection. Back off to a slow poll so a late connection can
    // still recover into "syncing"/"ready" without the idle cost.
    const SLOW_INTERVAL = 2_000;
    // GR-2394: if the initial subscribe never confirms, ActionCable's retry
    // logic exhausts after ~40s (patchSubscriptionRetry) and leaves the channel
    // closed; the send patch then silently swallows every failed update. Nothing
    // else flips readyState to "disconnected" before the first sync (observe-
    // SyncedProperty is only installed after it), so bound the initial connect
    // and surface the failure — gating save() and showing the reconnect banner.
    //
    // Count polls rather than reading a clock (xocs review): elapsed = polls *
    // FAST_INTERVAL is monotonic, immune to NTP steps / sleep-resume, and
    // testable under fake timers without mocking a wall clock. The timeout is
    // only ever checked while still fast-polling, so the interval is uniform.
    const CONNECT_TIMEOUT_POLLS = Math.ceil(45_000 / FAST_INTERVAL); // ~40s window + buffer
    let polls = 0;
    let wasConnected = false;

    const checkInitialSync = () => {
      polls += 1;
      // Check if the ActionCable channel is connected (subscribed)
      const isConnected = this.provider.channel?.subscription?.["identifier"] !== undefined;

      // Track connection state transition
      if (!wasConnected && isConnected) {
        wasConnected = true;
        this._timings.connectCompleted = performance.now();
        this._timings.syncStarted = performance.now();
        this.setReadyState("syncing");
        console.debug("[FormEditor] Channel connected, starting sync", {
          connectDuration: Math.round(
            (this._timings.connectCompleted || 0) - (this._timings.connectStarted || 0)
          ),
        });
      }

      if (this.provider.synced) {
        // First sync complete — stop polling and switch to reactive observation
        this._timings.syncCompleted = performance.now();
        this._timings.totalDuration =
          this._timings.syncCompleted - (this._timings.initStarted || 0);
        this.setReadyState("ready");
        this.logTimings();
        this.observeSyncedProperty();
        return;
      }

      // GR-2394: a stuck initial connection (subscribe never confirmed) is
      // surfaced as "disconnected". Keep polling so a late connection still
      // transitions to "syncing"/"ready" above.
      if (
        !isConnected &&
        this._readyState !== "disconnected" &&
        polls >= CONNECT_TIMEOUT_POLLS
      ) {
        console.warn(
          `[FormEditor] Initial connection did not confirm within ~${CONNECT_TIMEOUT_POLLS * FAST_INTERVAL}ms — flagging disconnected`
        );
        this.setReadyState("disconnected");
      }

      const interval = this._readyState === "disconnected" ? SLOW_INTERVAL : FAST_INTERVAL;
      this.syncCheckTimer = setTimeout(checkInitialSync, interval);
    };

    checkInitialSync();
  }

  // Intercept the provider's synced setter for reactive disconnect/reconnect
  // detection. The @y-rb/actioncable provider doesn't emit events, but its
  // setter fires when the ActionCable subscription's disconnected/connected
  // callbacks change sync state.
  private observeSyncedProperty(): void {
    const DEBOUNCE_MS = 6000; // 6s debounce to filter brief blips
    let currentSynced = true; // We know we're synced when this is called
    let disconnectTimer: ReturnType<typeof setTimeout> | null = null;

    Object.defineProperty(this.provider, 'synced', {
      get: () => currentSynced,
      set: (state: boolean) => {
        if (currentSynced !== state) {
          currentSynced = state;
          if (state) {
            // Sync restored — cancel pending disconnect
            if (disconnectTimer) {
              clearTimeout(disconnectTimer);
              disconnectTimer = null;
            }
            if (this._readyState === "disconnected") {
              console.debug("[FormEditor] Connection restored");
              this.setReadyState("ready");
            }
          } else {
            // Sync lost — debounce before flagging disconnection
            if (!disconnectTimer) {
              disconnectTimer = setTimeout(() => {
                disconnectTimer = null;
                if (!currentSynced && this._readyState !== "disconnected") {
                  console.warn("[FormEditor] Connection lost");
                  this.setReadyState("disconnected");
                }
              }, DEBOUNCE_MS);
            }
          }
        }
      },
      configurable: true,
    });

    this.syncedObserverCleanup = () => {
      if (disconnectTimer) {
        clearTimeout(disconnectTimer);
      }
      // Remove instance-level override, restoring prototype getter/setter
      delete (this.provider as any).synced;
    };
  }

  private logTimings(): void {
    const t = this._timings;
    const initDuration = (t.connectStarted || 0) - (t.initStarted || 0);
    const connectDuration = (t.connectCompleted || 0) - (t.connectStarted || 0);
    const syncDuration = (t.syncCompleted || 0) - (t.syncStarted || 0);

    console.debug("[FormEditor] Loading complete", {
      totalMs: Math.round(t.totalDuration || 0),
      breakdown: {
        initMs: Math.round(initDuration),
        connectMs: Math.round(connectDuration),
        syncMs: Math.round(syncDuration),
      },
    });
  }

  // Use browser online/visibilitychange events to trigger immediate reconnect
  // instead of waiting for the next Monitor backoff tick. This helps after:
  // - Network outages (online event fires when connectivity returns)
  // - Background tab throttling (timers are delayed; visibilitychange fires instantly)
  private observeBrowserConnectivity(): void {
    const cable = (this.consumer as any).cable;

    const triggerReconnect = (reason: string) => {
      if (this._readyState !== "disconnected") return;
      console.debug(`[FormEditor] ${reason}, triggering reconnect`);
      // reconnectNow() works when the Monitor is in 'disconnected'/'pending_reconnect';
      // cable.connect() works from 'closed' state (after token_expired flow).
      if (cable?.monitor?.reconnectNow?.()) return;
      cable?.connect?.()?.catch?.(() => {});
    };

    const handleOnline = () => triggerReconnect("Browser went online");
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        triggerReconnect("Tab became visible while disconnected");
      }
    };

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    this.browserConnectivityCleanup = () => {
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }

  dispose(): void {
    // Clean up connection monitor
    if (this.syncCheckTimer) {
      clearTimeout(this.syncCheckTimer);
      this.syncCheckTimer = null;
    }
    if (this.syncedObserverCleanup) {
      this.syncedObserverCleanup();
      this.syncedObserverCleanup = null;
    }
    if (this.browserConnectivityCleanup) {
      this.browserConnectivityCleanup();
      this.browserConnectivityCleanup = null;
    }

    // Clean up any pending lazy-load
    this.baseDocumentResolver = null;
    this.baseLoadPromise = null;

    // Closes the Y provider subscription and awareness
    this.provider.destroy();

    // Unsubscribe from other channels
    this.persistenceChannelSubscription?.unsubscribe();
    this.tracksChannelSubscription?.unsubscribe();
    this.sessionsChannelSubscription?.unsubscribe();

    // If assistant chat manages its own subscription, ensure it cleans up too
    this.assistantChat?.dispose?.();

    if (process.env.NODE_ENV !== "production") {
      delete (window as any).__formEditorDebug;
    }
  }

  static getInstance(args: FormChannelControlArgs): FormChannelControl {
    if (singleInstance) {
      return singleInstance;
    }
    singleInstance = new FormChannelControl(args);
    return singleInstance;
  }
}
